import type { Tables } from '@magicschool/supabase/types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocale } from '@/hooks/useLocale';
import { renderDate } from '@/util/date';
import type { RoomSchedulingStatus } from '../../types';

const STATUS_CONFIG = {
  'not-started': {
    intlId: 'magic-student.status-selector.schedule-not-started',
    dateField: 'scheduled_start_at' as const,
  },
  active: {
    intlId: 'magic-student.status-selector.schedule-in-progress',
    dateField: 'scheduled_end_at' as const,
  },
  ended: {
    intlId: 'magic-student.status-selector.schedule-ended',
    dateField: 'scheduled_end_at' as const,
  },
} as const;

/**
 * Hook to generate localized scheduling status labels
 */
export const useSchedulingLabel = (schedulingStatus: RoomSchedulingStatus, room: Partial<Tables<'rooms'>>) => {
  const { locale } = useLocale();

  return useMemo(() => {
    if (schedulingStatus === 'none') {
      return '';
    }

    const statusConfig = STATUS_CONFIG[schedulingStatus];
    if (!statusConfig) {
      return '';
    }

    const dateValue = room[statusConfig.dateField];
    const dateToDisplay = dateValue ? renderDate(locale, dateValue, room.timezone) : '';

    if (!dateToDisplay) {
      return '';
    }

    return (
      <FormattedMessage
        id={statusConfig.intlId}
        values={{
          date: (
            <time dateTime={dateValue ? new Date(dateValue).toISOString() : ''} key={dateToDisplay}>
              {dateToDisplay}
            </time>
          ),
        }}
      />
    );
  }, [schedulingStatus, room, locale]);
};
